<template>
	<div class="card mb-2">
		<div class="grid ml-0 mr-1 search-panel">
			<div class="flex flex-auto p-input-icon-left p-input-icon-right mt-1">
				<i class="pi pi-users" />
				<InputText v-model="query.keywords" placeholder="Keywords.." @input="onKeywordsChanged" @keyup.enter="onKeywordsChanged" />
				<i class="pi pi-search" />
			</div>
			<div class="flex flex-auto p-input-icon-left p-input-icon-right ml-1 mt-1">
				<i class="pi pi-globe" />
				<InputText v-model="query.city" placeholder="Location.." @input="onKeywordsChanged" @keyup.enter="onKeywordsChanged" />
				<i class="pi pi-search" />
			</div>
			<div class="flex align-items-center">
				<div class="flex ml-4 mr-4">
					<a class="css-image island north-island" v-bind:class="onlyNorthIsland ? 'north-island-icon':'north-island-disabled-icon'" v-tooltip.top="'Show companies operating in North Island'" @click="displayNorthIslandOnly" />
					<a class="css-image island south-island" v-bind:class="onlySouthIsland ? 'south-island-icon':'south-island-disabled-icon'" v-tooltip.top="'Show companies operating in South Island'" @click="displaySouthIslandOnly" />
				</div>
				<a @click="exportContacts" class="css-image excel-icon mt-1" v-tooltip.top="'Export to Excel'"></a>
			</div>
		</div>
		<div class="grid ml-0 mr-1 search-panel">
			<div class="flex align-items-center">
				<InputSwitch v-model="activeContactsOnly" class="mr-1 mt-1" @change="onQueryChanged" />
				<a class="mr-4 mt-1" @click="activeContactsOnly = !activeContactsOnly; onQueryChanged()">Only active</a>
			</div>
			<div class="flex align-items-center">
				<InputSwitch v-model="query.includeCustomers" class="mr-1 mt-1" @change="onQueryChanged" />
				<a class="mr-4 mt-1" @click="query.includeCustomers = !query.includeCustomers; onQueryChanged()">Customers</a>
			</div>
			<div class="flex align-items-center">
				<InputSwitch v-model="query.includeSuppliers" class="mr-1 mt-1" @change="onQueryChanged" />
				<a class="mr-4 mt-1" @click="query.includeSuppliers = !query.includeSuppliers; onQueryChanged()">Suppliers</a>
			</div>
			<div class="flex align-items-center">
				<InputSwitch v-model="query.includeOwners" class="mr-1 mt-1" @change="onQueryChanged" />
				<a class="mr-4 mt-1" @click="query.includeOwners = !query.includeOwners; onQueryChanged()">Owners</a>
			</div>
			<div class="flex align-items-center">
				<InputSwitch v-model="query.includeSiteContacts" class="mr-1 mt-1" @change="onQueryChanged" />
				<a class="mr-4 mt-1" @click="query.includeSiteContacts = !query.includeSiteContacts; onQueryChanged()">Site contacts</a>
			</div>
			<div class="flex align-items-center">
				<InputSwitch v-model="query.includeContactsWithoutEmail" class="mr-1 mt-1" @change="onQueryChanged" />
				<a class="mr-4 mt-1" @click="query.includeContactsWithoutEmail = !query.includeContactsWithoutEmail; onQueryChanged()">Missing emails</a>
			</div>
			<div class="flex align-items-center">
				<InputSwitch v-model="allowDuplicateEmails" class="mr-1 mt-1" @change="onQueryChanged" />
				<a class="mr-4 mt-1" @click="allowDuplicateEmails = !allowDuplicateEmails; onQueryChanged()">Duplicate emails</a>
			</div>
			<div class="flex align-items-center" style="margin-left: auto">
				<a @click="toggleContactsEditMode">
					<i class="pi pi-lock" v-if="!isEditModeEnabled" style="font-size: 1.75rem"></i>
					<i class="pi pi-lock-open" v-if="isEditModeEnabled" style="font-size: 1.75rem"></i>
				</a>
			</div>
		</div>
		<div class="table-wrapper" v-if="!loading">
			<DataTable stripedRows
					   :class="['p-datatable-sm', { 'mailing-list-datatable': isEditModeEnabled }]"
					   scrollHeight="flex"
					   responsiveLayout="scroll"
					   currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
					   :value="contacts"
					   :paginator="true"
					   :rows="100"
					   :scrollable="true"
					   :rowClass="rowClass"
					   :paginatorTemplate="paginatorTemplate"
					   :rowsPerPageOptions="[10,20,50,100,200]">

				<Column field="displayName" header="Contact" class="flex-wrap" v-if="!isEditModeEnabled">
					<template #body="{data}">
						<div class="clear" @click="onContactClick(data)">
							<span :class="{ 'compass-red': !!data.email && isActive(data), 'cursor-pointer': !!data.email}"> {{ data.displayName || '-' }}</span>
							<Badge class="small-grey-badge ml-1" value="inactive" v-if="!data.isActive" />
						</div>
					</template>
				</Column>
				<Column field="firstName" header="First name" class="flex-wrap flex-grow" v-if="isEditModeEnabled">
					<template #body="{data}">
						<div class="clear">
							<InputText v-model="data.firstName" />
						</div>
					</template>
				</Column>
				<Column field="lastName" header="Last name" class="flex-wrap flex-grow" v-if="isEditModeEnabled">
					<template #body="{data}">
						<div class="clear">
							<InputText v-model="data.lastName" />
						</div>
					</template>
				</Column>
				<Column field="jobTitle" header="Job title" class="flex-wrap flex-grow" v-if="isEditModeEnabled && windowWidth >= 1200">
					<template #body="{data}">
						<div class="clear">
							<InputText v-model="data.jobTitle" />
						</div>
					</template>
				</Column>
				<Column field="companyName" header="Company" class="flex-wrap flex-grow" v-if="windowWidth >= 1500">
					<template #body="{data}">
						<div class="clear" v-if="data.companyId && !data.isCompanyDeleted">
							<router-link :to="!data.isCompanyDeleted ? { name: 'CompanyDetails', params: { companyId: data.companyId } } : null">
								{{ data.companyName }}
							</router-link>
							<Badge class="small-grey-badge ml-1" value="inactive" v-if="!data.isCompanyActive" />
						</div>
						<span v-else>-</span>
					</template>
				</Column>
				<Column field="mobile" header="Mobile" class="flex-wrap flex-grow">
					<template #body="{data}">
						<div class="clear">
							<span v-if="!isEditModeEnabled">{{ data.mobile || '-'}}</span>
							<InputText v-model="data.mobile" v-if="isEditModeEnabled" />
						</div>
					</template>
				</Column>
				<Column field="ddi" header="Phone" class="flex-wrap flex-grow" v-if="isEditModeEnabled && windowWidth >= 1200">
					<template #body="{data}">
						<div class="clear">
							<InputText v-model="data.ddi" />
						</div>
					</template>
				</Column>
				<Column field="email" header="Email" class="flex-wrap flex-grow">
					<template #body="{data}">
						<div class="clear">
							<span v-if="!isEditModeEnabled">{{ data.email || '-'}}</span>
							<InputText v-model="data.email" v-if="isEditModeEnabled" />
						</div>
					</template>
				</Column>
				<Column field="city" header="Location" class="flex-wrap" v-if="windowWidth >= 1200 && !isEditModeEnabled">
					<template #body="{data}">
						{{ data.city || '-' }}
						<Badge class="css-image new-zealand-icon island-badge ml-1" v-tooltip.top="'Operates in North & South Island'" v-if="data.island == 'NI SI'" />
						<Badge class="css-image north-island-icon island-badge ml-1" v-tooltip.top="'Operates in North Island'" v-else-if="data.island == 'NI'" />
						<Badge class="css-image south-island-icon island-badge ml-1" v-tooltip.top="'Operates in South Island'" v-else-if="data.island == 'SI'" />
					</template>
				</Column>
				<Column field="delete" v-if="isEditModeEnabled && windowWidth >= 992">
					<template #body="{data}">
						<div :title="getTooltipText(data.isCompanyActive, data.isActive)">
							<Button icon="pi pi-trash"  @click="archiveContact(data)" v-if="!loading && data.isActive" />
							<Button icon="pi pi-refresh" @click="restoreContact(data)" v-if="!loading && !data.isActive" :disabled="!data.isCompanyActive"  />
						</div>
					</template>
				</Column>
			</DataTable>
		</div>
		<ProgressSpinner v-if="loading" />
	</div>

	<div class="flex justify-content-end" v-if="isEditModeEnabled">
		<Button class="sticky-button" icon="pi pi-save" label="Save" @click="saveContacts" v-if="!loading" :disabled="!hasChanges" />
	</div>

	<!--Selected contact details modal-->
	<ContactDetailsPopup :selectedContact="selectedContact"
						 @updateshowContactDetailsPopup="showContactDetailsPopup = $event;"
						 v-if="showContactDetailsPopup" />
</template>

<script lang="ts">
	import Toast from '@/utils/Toast';
	import { Options, Vue } from 'vue-class-component';
	import { CompassApi } from '../api-client/CompassApiProxy';
	import { ContactListMember, ContactListSearchQuery, GetContactListExcelExportQuery, GetContactListQuery, 
		UpdateContactsCommand, ArchiveContactCommand, RestoreContactCommand } from '../api-client/api-client';
	import { DownloadFromFileResponse } from '../utils/DownloadFromFileResponse';
	import { ConfirmationOptions } from 'primevue/confirmationoptions';
	import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
	import { allowLeaveOnlyWithConfirmationOrNoChanges } from '@/components/LeaveWithoutSavingPrompt';

	Vue.registerHooks([
		'beforeRouteLeave',
	])

	@Options({
		name: 'MailingList',
		components: {},
	mounted() {
		window.addEventListener('resize', this.onResize);
	},

	beforeUnmount() {
		window.removeEventListener('resize', this.onResize);
		}
	})
	export default class MailingList extends Vue {

		timeoutId: number = -1;

		isEditModeEnabled: boolean = false;

		loading: boolean = false;

		onlyNorthIsland: boolean = false;

		onlySouthIsland: boolean = false;

		showContactDetailsPopup = false;

		query: ContactListSearchQuery = new ContactListSearchQuery();

		contacts: Array<ContactListMember> = [];

		selectedContact: ContactListMember | null = null;

		contactsOnLoadJson: string = "";

		windowWidth: number = window.innerWidth;

		created(): void {
			this.query.includeSuppliers = true;
			this.query.includeCustomers = true;
			this.query.includeOwners = true;
			this.query.ignoreDuplicateEmailAddresses = true;
			this.query.includeInactive = false;
			this.query.includeContactsWithoutEmail = false;
			this.query.includeSiteContacts = false;

			this.search();
		}

		async search(): Promise<void> {

			const apiClient = CompassApi.contactApi(this.$store);
			const recordLimit = 10000;

			this.loading = true;

			const request = new GetContactListQuery();
			request.query = this.query
			request.query.recordLimit = recordLimit;

			return await apiClient
				.searchContacts(request)
				.then(response => {
					this.contacts = response;
				})
				.catch(error => {
					Toast.ApiError(`Error retrieving contacts`, error);
				})
				.finally(() => {
					this.contactsOnLoadJson = JSON.stringify(this.contacts, null, 1);
					this.loading = false;
				});
		}

		async exportContacts(): Promise<void> {
			const apiClient = CompassApi.contactApi(this.$store);
			const request = new GetContactListExcelExportQuery();
			request.query = this.query;

			return await apiClient
				.exportContacts(request)
				.then(response => {
					if (!response) return;

					Toast.RemoveAllGroups();
					Toast.Success(`Success`, `Contact records have been successfully exported`);

					DownloadFromFileResponse(response);
				})
				.catch(error => {
					Toast.ApiError(`Error exporting contacts to Excel`, error);
				})
				.finally(() => {
					this.loading = false;
				});
		}

		async saveContacts() {
			this.loading = true;

			const apiClient = CompassApi.contactApi(this.$store);
			const updateContactsCommand = new UpdateContactsCommand();
			updateContactsCommand.contacts = this.contacts;

			apiClient.updateContacts(updateContactsCommand)
				.then(() => {
					Toast.RemoveAllGroups();
					Toast.Success('Updated contacts', 'Contact details have been updated succesfully');
				})
				.catch(error => {
					this.loading = false;
					Toast.ApiError('Error updating contact details ', error);
				})
				.finally(() => {
					this.isEditModeEnabled = !this.isEditModeEnabled;
					this.search();
					this.loading = false;
				});
		}


		async archiveContact(contact: ContactListMember) {
			const userConfirmation = await this.archiveContactConfirmation(contact)
			if (!userConfirmation)
				return;

			this.loading = true;

			const apiClient = CompassApi.contactApi(this.$store);
			const archiveContactCommand = new ArchiveContactCommand();
			archiveContactCommand.contact = contact;

			apiClient.archiveContact(archiveContactCommand)
				.then(() => {
					Toast.RemoveAllGroups();
					Toast.Success('Archived contact', 'Contact has been succesfully archived');
				})
				.catch(error => {
					this.loading = false;
					Toast.ApiError(`Error archiving contact: ${contact.displayName} `, error);
				})
				.finally(() => {
					this.toggleContactsEditMode();
					this.loading = false;
				});
		}

		async restoreContact(contact: ContactListMember) {
			this.loading = true;

			const apiClient = CompassApi.contactApi(this.$store);
			const restoreContactCommand = new RestoreContactCommand();
			restoreContactCommand.contact = contact;

			apiClient.restoreContact(restoreContactCommand)
				.then(() => {
					Toast.RemoveAllGroups();
					Toast.Success('Restored contact', 'Contact has been succesfully restored');
				})
				.catch(error => {
					this.loading = false;
					Toast.ApiError(`Error restoring contact: ${contact.displayName} `, error);
				})
				.finally(() => {
					this.toggleContactsEditMode();
					this.loading = false;
				});
		}

		async displayNorthIslandOnly(): Promise<void> {
			this.onlyNorthIsland = !this.onlyNorthIsland;
			this.onlySouthIsland = false;

			this.query.island = this.onlyNorthIsland ? `NI` : undefined;

			return await this.onQueryChanged();
		}

		async displaySouthIslandOnly(): Promise<void> {
			this.onlySouthIsland = !this.onlySouthIsland;
			this.onlyNorthIsland = false;

			this.query.island = this.onlySouthIsland ? `SI` : undefined;

			return await this.onQueryChanged();
		}

		async displayActiveContactsOnly(): Promise<void> {
			this.activeContactsOnly = !this.activeContactsOnly;
			return await this.onQueryChanged();
		}

		async ignoreDuplicateEmailAddresses(): Promise<void> {
			this.query.ignoreDuplicateEmailAddresses = !this.query.ignoreDuplicateEmailAddresses;
			return await this.onQueryChanged();
		}

		async includeCustomers(): Promise<void> {
			this.query.includeCustomers = !this.query.includeCustomers;
			return await this.onQueryChanged();
		}

		async includeSuppliers(): Promise<void> {
			this.query.includeSuppliers = !this.query.includeSuppliers;
			return await this.onQueryChanged();
		}

		async includeOwners(): Promise<void> {
			this.query.includeOwners = !this.query.includeOwners;
			return await this.onQueryChanged();
		}

		async includeSiteContacts(): Promise<void> {
			this.query.includeSiteContacts = !this.query.includeSiteContacts;
			return await this.onQueryChanged();
		}

		async includeContactsWithoutEmail(): Promise<void> {
			this.query.includeContactsWithoutEmail = !this.query.includeContactsWithoutEmail;
			return await this.onQueryChanged();
		}

		async onKeywordsChanged(): Promise<void> {
			clearTimeout(this.timeoutId);

			this.timeoutId = setTimeout(async () => {
				return await this.onQueryChanged();
			}, 500);
		}

		async onQueryChanged(): Promise<void> {
			if (this.isEditModeEnabled) {

				if (this.hasChanges) {
					const userConfirmation = await this.discardChangesConfirmation()
					if (!userConfirmation)
						return;
				}
				this.isEditModeEnabled = !this.isEditModeEnabled;
			}

			return await this.search()
		}

		async toggleContactsEditMode() {

			if (this.isEditModeEnabled && this.hasChanges) {
				const userConfirmation = await this.discardChangesConfirmation()
				if (!userConfirmation)
					return;
			} 

			this.isEditModeEnabled = !this.isEditModeEnabled;

			this.query.ignoreDuplicateEmailAddresses = false;

			return await this.search()
		}

		async discardChangesConfirmation(): Promise<boolean> {
			return new Promise((resolve) => {
				this.$confirm.require({
					header: 'Unsaved changes',
					message: `Are you sure you want to discard these unsaved changes?`,
					accept: () => {
						resolve(true);
					},
					reject: () => {
						resolve(false);
					}
				} as ConfirmationOptions);
			});
		}

		async archiveContactConfirmation(contact: ContactListMember): Promise<boolean> {
			return new Promise((resolve) => {
				this.$confirm.require({
					header: 'Delete contact',
					message: `Are you sure you want to delete: ${contact.displayName}?`,
					accept: () => {
						resolve(true);
					},
					reject: () => {
						resolve(false);
					}
				} as ConfirmationOptions);
			});
		}

		onResize() {
			this.windowWidth = window.innerWidth;
		}

		isActive(data: ContactListMember): boolean {
			return data.isActive && data.isCompanyActive ? true : false;
		}

		rowClass(data: ContactListMember): string {
			return this.isActive(data) ? "" : "row-inactive";
		}

		onContactClick(contact: ContactListMember) {

			// Contacts without emails cannot be loaded
			if (!contact.email)
				return;

			this.selectedContact = contact;

			this.showContactDetailsPopup = true;
		}

		getTooltipText(isCompanyActive: boolean, isContactActive: boolean): string {
			return !isContactActive && !isCompanyActive ? "Company needs to be active first" : "";
		}

		beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
			this.showContactDetailsPopup = false;
			allowLeaveOnlyWithConfirmationOrNoChanges(this, this.hasChanges, next);
		}
		
		get isDesktop() {
			return window.innerWidth >= 992;
		}

		get paginatorTemplate() {
			// todo: repeated in a bunch of places and should be a component. also needs to support server-side pagination.
			if (!this.isDesktop)
				return `PrevPageLink NextPageLink`;

			return `CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown`;
		}

		get activeContactsOnly() {
			return this.query.includeInactive ? false : true;
		}

		set activeContactsOnly(value: boolean) {
			this.query.includeInactive = !value;
		}

		get allowDuplicateEmails() {
			return this.query.ignoreDuplicateEmailAddresses ? false : true;
		}

		set allowDuplicateEmails(value: boolean) {
			this.query.ignoreDuplicateEmailAddresses = !value;
		}

		get hasChanges(): boolean {
			const currentJSON = JSON.stringify(this.contacts, null, 1);
			return (currentJSON != this.contactsOnLoadJson);
		}
	}
</script>
<style lang="scss">


	div {
		&.search-panel {
			margin-bottom: 10px;
		}

		&.table-wrapper {
			width: 100%;
			height: calc(100vh - 260px);
		}

		&.clear {
			width: 100%;
			overflow: hidden !important;
			text-overflow: ellipsis !important;
		}
	}

	a {
		&.island {
			position: absolute;
			margin-top: -15px;
			height: 20px;
			width: 30px;

			&.north-island {
				margin-top: -15px;
			}

			&.south-island {
				margin-top: 5px;
			}
		}
	}

	td {
		&.flex-wrap {
			overflow: hidden;
			text-overflow: ellipsis;
			width: 0;
			flex-wrap: wrap;
			flex-basis: 0;
			flex: 1 1 0;
			flex-wrap: wrap;
			white-space: pre;
		}
	}
</style>
